import { PERMISSIONS } from '@/constants/permissions';
import { ROLES } from '@/constants/roles';
import { authUtils } from '@/utils/auth';

/**
 * Payables / Transactions and Receivables are bookkeeper work.
 *
 * Settings catalog items under Money (`canSeeFinancialHub`)
 * stay Owner + Estimator — that is catalog / company-setup, not this spine.
 * Invoice Review Routing / cascade / pricing rules / cost-drift now sit in
 * that same Settings group but keep their own role lists (not this gate,
 * and not the catalog hub gate).
 * Do not gate this work on PROCUREMENT.VIEW alone: Estimator still holds
 * that leftover grant and must not see or post bookkeeper work.
 */

export type BookkeeperAccessFlags = {
  isOwner: boolean;
  isBookkeeper: boolean;
  isEstimator: boolean;
  hasLegacyFinancialPermission: boolean;
};

export function evaluateBookkeeperAccess(flags: BookkeeperAccessFlags): boolean {
  if (flags.isOwner || flags.isBookkeeper) return true;
  if (flags.isEstimator) return false;
  return flags.hasLegacyFinancialPermission;
}

export function evaluateEstimatorBlockedFromBookkeeperWork(
  flags: Pick<BookkeeperAccessFlags, 'isOwner' | 'isBookkeeper' | 'isEstimator'>,
): boolean {
  return flags.isEstimator && !flags.isOwner && !flags.isBookkeeper;
}

function viewerHasRole(slug: string, name: string): boolean {
  if (typeof authUtils.hasRole === 'function') {
    return authUtils.hasRole(slug) || authUtils.hasRole(name);
  }
  const roles =
    typeof authUtils.getUserRoles === 'function' ? authUtils.getUserRoles() : [];
  return roles.includes(slug) || roles.includes(name);
}

function viewerHasPermission(permission: string): boolean {
  return (
    typeof authUtils.hasPermission === 'function' &&
    authUtils.hasPermission(permission)
  );
}

function viewerFlags(): BookkeeperAccessFlags {
  const user =
    typeof authUtils.getCurrentUser === 'function'
      ? authUtils.getCurrentUser()
      : null;
  return {
    isOwner:
      (typeof authUtils.isOrganizationOwner === 'function' &&
        authUtils.isOrganizationOwner(user)) ||
      user?.isOwner === true ||
      user?.isPlatformAdmin === true ||
      viewerHasRole(ROLES.OWNER.slug, ROLES.OWNER.name),
    isBookkeeper: viewerHasRole(ROLES.BOOKKEEPER.slug, ROLES.BOOKKEEPER.name),
    isEstimator: viewerHasRole(ROLES.ESTIMATOR.slug, ROLES.ESTIMATOR.name),
    hasLegacyFinancialPermission:
      viewerHasPermission(PERMISSIONS.PROCUREMENT.VIEW) ||
      viewerHasPermission(PERMISSIONS.BUDGET.VIEW) ||
      viewerHasPermission(PERMISSIONS.BUDGET.VIEW_FINANCIALS),
  };
}

/** Owner, Bookkeeper, or a non-Estimator with existing financial perms. */
export function canAccessBookkeeperWork(): boolean {
  return evaluateBookkeeperAccess(viewerFlags());
}

/** Estimator who is not also Owner/Bookkeeper — hide and forbid the work. */
export function isEstimatorBlockedFromBookkeeperWork(): boolean {
  return evaluateEstimatorBlockedFromBookkeeperWork(viewerFlags());
}

export function viewerIsBookkeeper(): boolean {
  return viewerFlags().isBookkeeper;
}
